import { useId, useState } from 'react';

import { Button } from '@ds/core';
import { Modal } from '@ds/feedback';
import { FormField, Input, Select, Switch } from '@ds/forms';
import { centavosComSinalDoTexto, textoDosCentavos } from '@/comuns/dinheiro';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import { TIPOS_DE_CONTA } from '@/data/financeiroAvulso';
import type { ContaNoCadastro } from '@/data/financeiroAvulso';
import type { ContaFinanceira } from '@/data/tipos';

export interface ContaModalProps {
    alvo: ContaNoCadastro | 'nova';
    onFechar: () => void;
    onSalva: () => void;
}

/**
 * Nova conta e edição. O saldo INICIAL é o que havia antes de a clínica usar a
 * VecchIA — o saldo de hoje é ele mais o que já liquidou, e não se digita.
 */
export function ContaModal({ alvo, onFechar, onSalva }: ContaModalProps) {
    const aviso = useAviso();
    const idNome = useId();
    const idTipo = useId();
    const idBanco = useId();
    const idAgencia = useId();
    const idNumero = useId();
    const idSaldo = useId();
    const nova = alvo === 'nova';

    const inicial = {
        nome: nova ? '' : alvo.nome,
        tipo: (nova ? 'banco' : alvo.tipo) as ContaFinanceira['tipo'],
        banco: nova ? '' : alvo.banco ?? '',
        agencia: nova ? '' : alvo.agencia ?? '',
        numero: nova ? '' : alvo.numero ?? '',
        saldo: nova ? '0,00' : textoDosCentavos(alvo.saldoInicialCentavos),
        ativa: nova ? true : alvo.ativa,
    };
    const [campos, setCampos] = useState(inicial);
    const [salvando, setSalvando] = useState(false);
    const mudar = <C extends keyof typeof campos>(campo: C, valor: (typeof campos)[C]) => setCampos((c) => ({ ...c, [campo]: valor }));

    const saldoCentavos = centavosComSinalDoTexto(campos.saldo);
    const pode = Boolean(campos.nome.trim()) && saldoCentavos !== null;

    const salvar = async () => {
        if (!pode || saldoCentavos === null) return;
        setSalvando(true);
        try {
            await dados().salvarConta(nova ? null : alvo.id, {
                nome: campos.nome, tipo: campos.tipo, saldoInicialCentavos: saldoCentavos, ativa: campos.ativa,
                ...(campos.banco.trim() ? { banco: campos.banco } : {}),
                ...(campos.agencia.trim() ? { agencia: campos.agencia } : {}),
                ...(campos.numero.trim() ? { numero: campos.numero } : {}),
            });
            aviso({ title: nova ? 'Conta cadastrada' : 'Conta atualizada', description: campos.nome.trim() });
            onSalva();
            onFechar();
        } catch (e) {
            aviso({ title: 'Não foi possível salvar a conta', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal
            open width={520} onClose={onFechar}
            title={nova ? 'Nova conta financeira' : 'Editar conta'}
            description="Contas alimentam o extrato, o fluxo de caixa e as formas de pagamento."
            dirty={JSON.stringify(campos) !== JSON.stringify(inicial)} onSave={() => { void salvar(); }}
            footer={(
                <>
                    <Button variant="ghost" onClick={onFechar}>Cancelar</Button>
                    <Button onClick={() => { void salvar(); }} disabled={!pode || salvando}>{salvando ? 'Salvando…' : nova ? 'Cadastrar' : 'Salvar'}</Button>
                </>
            )}
        >
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: 14 }}>
                <FormField label="Nome da conta" htmlFor={idNome} required style={{ gridColumn: '1 / -1' }}>
                    <Input id={idNome} value={campos.nome} onChange={(v) => mudar('nome', v)} placeholder="Ex.: Banco padrão" />
                </FormField>
                <FormField label="Tipo" htmlFor={idTipo}>
                    <Select id={idTipo} value={campos.tipo} onChange={(v) => mudar('tipo', v as ContaFinanceira['tipo'])} options={TIPOS_DE_CONTA.map((t) => ({ value: t.valor, label: t.rotulo }))} />
                </FormField>
                <FormField label="Instituição" htmlFor={idBanco}>
                    <Input id={idBanco} value={campos.banco} onChange={(v) => mudar('banco', v)} placeholder="Itaú, Stone, Nubank…" />
                </FormField>
                <FormField label="Agência" htmlFor={idAgencia}>
                    <Input id={idAgencia} value={campos.agencia} onChange={(v) => mudar('agencia', v)} />
                </FormField>
                <FormField label="Número da conta" htmlFor={idNumero}>
                    <Input id={idNumero} value={campos.numero} onChange={(v) => mudar('numero', v)} />
                </FormField>
                <FormField label="Saldo inicial" htmlFor={idSaldo} hint="Negativo para cartão de crédito com fatura em aberto" error={saldoCentavos === null ? 'Valor inválido' : undefined} style={{ gridColumn: '1 / -1' }}>
                    <Input id={idSaldo} value={campos.saldo} onChange={(v) => mudar('saldo', v)} inputMode="decimal" iconLeft={<span style={{ fontSize: 13 }}>R$</span>} invalid={saldoCentavos === null} />
                </FormField>
                <Switch
                    checked={campos.ativa} onChange={(v) => mudar('ativa', v)} label="Conta ativa" style={{ gridColumn: '1 / -1' }}
                    description="Desativada, sai das listas de escolha. O que já passou por ela continua no extrato."
                />
            </div>
        </Modal>
    );
}
